import { useCallback, useLayoutEffect, useRef } from "react"
import { useNavigate, type NavigateFunction } from "react-router"

/**
 * `useNavigate`, который не меняется по ссылке всё время жизни компонента.
 *
 * Страницы приложения рендерятся во вложенном `useRoutes` (каркас — маршрут `*`
 * data-роутера), а там `useNavigate` отдаёт новую функцию при каждой смене пути.
 * Из-за этого обработчики в `useCallback` с `navigate` в зависимостях пересоздавались
 * на каждом переходе, и дочерние `observer` / `memo` (списки на AG Grid) перерисовывались
 * при каждом открытии и закрытии карточки, а эффект с `navigate` в зависимостях
 * перезапускался — например, повторно грузил список.
 *
 * Эта функция стабильна: её можно ставить в зависимости, не думая. Вызывает она
 * актуальный `navigate` — относительные пути считаются от текущего адреса.
 * Как и `navigate`, вызывать её можно в обработчиках и эффектах, но не во время рендера.
 */
export function useStableNavigate(): NavigateFunction {
    const navigate = useNavigate()
    const latest = useRef(navigate)

    useLayoutEffect(() => {
        latest.current = navigate
    })

    return useCallback(
        ((to: any, options?: any) =>
            typeof to === "number" ? latest.current(to) : latest.current(to, options)) as NavigateFunction,
        []
    )
}
